import { useMemo, useState } from "react";

import { IgAvatar } from "@intelligems/ig-theme/IgAvatar";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import {
  ImageList,
  ImageListItem,
  ImageListItemBar,
  useTheme,
} from "@mui/material";
import { useDialog } from "@src/hooks/dialog";

const getImageLabel = (src: string): string => {
  const filename = src.split("/").pop()?.split("?").shift();
  return filename || "Image";
};

export const MultiImageSelectDialog = () => {
  const dialog = useDialog("multiImageSelect");
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const allImages = dialog.props?.images ?? [];
  const onSelect = dialog.props?.onSelect;

  // Filter to images with valid src
  const images = useMemo(
    () => allImages.filter((img) => !!img.src),
    [allImages]
  );

  const handleClose = () => {
    setSelectedIndex(null);
    dialog.close();
  };

  const handleConfirm = () => {
    if (selectedIndex !== null && images[selectedIndex] && onSelect) {
      onSelect(images[selectedIndex]);
    }
    handleClose();
  };

  return (
    <IgDialog
      showDialog={dialog.isOpen}
      title="Select an image to replace"
      handleClose={handleClose}
      handleYesClick={handleConfirm}
      yesButtonText="Confirm"
      yesButtonDisabled={selectedIndex === null}
      noButtonText="Cancel"
      handleNoClick={handleClose}
      maxWidth="md"
      dialogHeight={null}
      shouldNotClose
      rawContent={
        <ImageList
          cols={5}
          gap={16}
          sx={{
            m: 0,
            p: 2,
            maxHeight: "400px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {images.map((image, index) => (
            <ImageListItem
              key={index}
              onClick={() => setSelectedIndex(index)}
              sx={{
                cursor: "pointer",
                width: "140px",
                minWidth: "140px",
                maxWidth: "140px",
              }}
            >
              <IgAvatar
                variant="rounded"
                src={image.src}
                alt={`Image ${index + 1}`}
                sx={{
                  width: "140px",
                  height: "140px",
                  borderRadius: "4px",
                  border: `2px solid ${
                    selectedIndex === index
                      ? theme.igBorders.accent.default
                      : theme.igBorders.core.default
                  }`,
                  transition: "border-color 0.2s",
                  backgroundColor:
                    theme.igBackgroundColors.interactive.neutral.default,
                  "&:hover": {
                    borderColor:
                      selectedIndex === index
                        ? theme.igBorders.accent.default
                        : theme.palette.grey[400],
                  },
                }}
              />
              <ImageListItemBar
                position="below"
                title={getImageLabel(image.src ?? "")}
                sx={{
                  "& .MuiImageListItemBar-titleWrap": {
                    paddingBottom: "4px",
                    paddingLeft: 0,
                    paddingRight: 0,
                  },
                  "& .MuiImageListItemBar-title": {
                    fontSize: "14px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    maxWidth: "140px",
                  },
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      }
    />
  );
};
